<template>
  <el-form
    ref="formRef"
    require-asterisk-position="right"
    label-position="top"
    :model="form"
    :rules="formRules"
  >
    <div class="flex">
      <div class="oa-form-left label">
        <el-form-item prop="contractTempId" label="合同模板名称">
          <el-select
            v-model="form.contractTempId"
            filterable
            clearable
            :disabled="Boolean(route.query.contractId)"
            placeholder="请选择"
            @change="handleChangeContractTemp"
          >
            <el-option
              v-for="item in templateList"
              :key="item.id"
              :label="item.tempName"
              :value="item.id"
            >
              {{ item.tempName }}
            </el-option>
          </el-select>
          <div class="tip1">
            <el-link type="primary" :underline="false" @click="searchTemplate">
              查看模版
            </el-link>
          </div>
        </el-form-item>
        <el-form-item prop="endTime" label="签约截止日期">
          <div class="tip">
            <el-popover
              width="353"
              placement="right"
              trigger="hover"
            >
              <template #reference>
                <svg-icon class="!w-[14px] ml-[15px] main-primary-text" icon="mingcute:warning-line" />
              </template>
              <div class="text-[##6B7891]">
                签约截止时间前，要求所有参与方签署完成
              </div>
            </el-popover>
          </div>
          <el-date-picker
            v-model="form.endTime"
            style="width: 100%;"
            type="date"
            clearable
            placeholder="请选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
      </div>
      <div class="oa-form-right label">
        <el-form-item prop="contractSubject" label="合同主题">
          <div class="tip2">
            <el-popover
              width="380"
              placement="right"
              trigger="hover"
            >
              <template #reference>
                <svg-icon class="!w-[14px] ml-[15px] main-primary-text" icon="mingcute:warning-line" />
              </template>
              <div class="text-[##6B7891]">
                合同主题：此合同的名称 <br>
                建议格式：【业务类型】-【合同类型】-【对方名称】<br>
                示例：“撮合-自由职业者合同-李三” 
              </div>
            </el-popover>
          </div>
          <el-input
            v-model="form.contractSubject"
            clearable
            autocomplete="nony"
            placeholder="请输入"
            max-length="100"
          />
        </el-form-item>
        <el-form-item prop="expireTime" label="合同到期日期">
          <div class="tip">
            <el-popover
              width="353"
              placement="right"
              trigger="hover"
            >
              <template #reference>
                <svg-icon class="!w-[14px] ml-[15px] main-primary-text" icon="mingcute:warning-line" />
              </template>
              <div class="text-[##6B7891]">
                合同到期日期为签署合同的履约结束日期，不影响签署过程
              </div>
            </el-popover>
          </div>
          <el-date-picker
            v-model="form.expireTime"
            style="width: 100%;"
            type="date"
            clearable
            placeholder="请选择日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD HH:mm:ss"
          />
        </el-form-item>
      </div>
    </div>
  </el-form>
</template>

<script setup lang="ts">
import { PropType, ref, watch } from 'vue';
import { SvgIcon } from '@vue3/components';
import { queryContractTempPreviewTempUrl, queryContractCreateDropDownTemp, queryContractFreeAuthTopAgentContractTemp } from '@core/main';
import { ElMessage } from 'element-plus';
import { IContractFreeDetailVO } from '@core/main/types/contracts/contracts';
import { useRoute } from 'vue-router';

const props = defineProps({
  data: {
    type: Object as PropType<IContractFreeDetailVO>,
    default: () => {
      return {} as IContractFreeDetailVO;
    },
  },
  enableEnterKey: {
    type: Boolean,
    default: true,
  },
  /** 自动滚动到错误位置 */
  autoScrollToError: {
    type: Boolean,
    default: true,
  },
  from: {
    type: String as PropType<'saas' | 'company' | 'agent' | 'server'>,
    default: 'saas',
  },
});

const emit = defineEmits(['change-temp']);

const route = useRoute();
const templateList = ref([]);
const form = ref({
  contractTempId: props.data?.contractId || null,
  contractSubject: props.data?.contractSubject || null,
  endTime: props.data?.endTime || null,
  expireTime: props.data?.expireTime || null,
} as any);
const formRef = ref();

const formRules = {
  contractTempId: [
    {
      required: true,
      message: '请选择合同模板名称',
      trigger: 'blur',
    },
  ],
  contractSubject: [
    {
      required: true,
      message: '请输入合同主题',
      trigger: 'blur',
    },
  ],
  endTime: [
    {
      required: true,
      message: '请选择签约截止日期',
      trigger: 'blur',
    },
  ],
  expireTime: [
    {
      required: true,
      message: '请选择合同到期日期',
      trigger: 'blur',
    },
  ],
};

function handleChangeContractTemp(id) {
  emit('change-temp', id);
  let filter = [];

  filter = templateList.value.filter((item) => {
    return item.id == id; 
  });

  if (filter.length) {
    form.value.contractSubject = filter[0]?.tempName;
  }
}

async function searchTemplate() {
  try {
    const url = await queryContractTempPreviewTempUrl(form.value.contractTempId);

    window.open(url);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function queryplatformOption() {
  try {
    if (props.from == 'agent') {
      templateList.value = await queryContractFreeAuthTopAgentContractTemp();
    } else {
      templateList.value = await queryContractCreateDropDownTemp();
    }
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

queryplatformOption();

watch(
  () => props.data,
  () => {
    if (route.query.contractId) {
      form.value = {
        contractTempId: props.data?.contractTempId || null,
        contractSubject: props.data?.contractSubject || null,
        endTime: props.data?.endTime || null,
        expireTime: props.data?.expireTime || null,
      };
    }
  },
);

async function validate(callback?: () => void) {
  let valid = false;

  if (!formRef.value) return valid;

  await formRef.value.validate((v) => {
    if (v) {
      valid = true;
      callback && callback();
    }
  });

  return valid;
}

function resetFormValues() {

  formRef.value?.resetFields?.();
}

defineExpose({
  /**
   * 信息校验
   * return {boolean} true 通过， false失败
  */
  validate,
  /** 获取表单数据 */
  getValues: () => form.value,
  /** 表单数据重置（base props.data） */
  reset: resetFormValues,
});

</script>

<style lang="scss" scoped>
.oa-form-left {
  width: 432px;
}

.oa-form-right {
  width: 432px;
  margin-left: 114px;
}

.label {
  position: relative;

  & .tip {
    position: absolute;
    top: -30px;
    left: 85px;

  }
  & .tip1 {
    position: absolute;
    top: 0px;
    right: -65px;
  }
  & .tip2 {
    position: absolute;
    top: -30px;
    left: 58px;
  }
}

</style>
